import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import { Observable, TimeoutError, catchError, switchMap, take, throwError, timeout } from 'rxjs';
import { AuthService } from '../services/auth/auth.service';
import { HmacSignatureService } from '../services/security/hmac-signature.service';
import { isRefreshTokenInvalidError } from '../utils/auth-error.utils';
import { SKIP_AUTH } from './auth.context';

const AUTH_RETRY_CONTEXT = new HttpContextToken<boolean>(() => false);

// Export if you want per-request override from callers.
export const REQUEST_TIMEOUT_MS = new HttpContextToken<number>(() => 10000);

// Re-exported for backward compatibility; the token itself lives in a dependency-free module.
export { SKIP_AUTH };

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private authService?: AuthService;
  private readonly injector = inject(Injector);
  private readonly hmacSignatureService = inject(HmacSignatureService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // 1) Skip static files and explicitly opted-out (public content) requests completely.
    if (this.isStaticAsset(req.url) || req.context.get(SKIP_AUTH)) {
      return next.handle(req);
    }

    // 2) Add HMAC for eligible POST payloads.
    const requestWithHmac = this.shouldSignWithHmac(req) ? this.addHmacHeaders(req) : req;

    // 3) Public/auth endpoints should not receive Bearer injection/retry logic.
    if (this.isAuthEndpoint(requestWithHmac.url) || this.isPublicEndpoint(requestWithHmac.url)) {
      return next.handle(requestWithHmac);
    }

    // File downloads can be large (10 MB+) — give them at least 90 s regardless
    // of whatever the caller set (or the 10 s default).
    const isFileDownload = req.url.includes('/file/') && req.url.includes('/download');

    // Single-communication GraphQL queries fetch the full email HTML body, which can
    // be large — give them at least 20 s to match the service-level timeout.
    const isCommunicationBody = req.url.includes('graphql') && req.url.includes('Communication');

    const timeoutMs = isFileDownload
      ? Math.max(req.context.get(REQUEST_TIMEOUT_MS), 90_000)
      : isCommunicationBody
        ? Math.max(req.context.get(REQUEST_TIMEOUT_MS), 20_000)
        : req.context.get(REQUEST_TIMEOUT_MS);

    // 4) Non-auth endpoints require access token; attempt refresh if missing/expired.
    return this.getAuthService()
      .getToken()
      .pipe(
        take(1),
        switchMap((token) => {
          if (!token) {
            // User is explicitly unauthenticated (public pages: login, reset, activate, need-help, etc.)
            // — do not attempt a token refresh, just pass the request through as-is.
            if (this.getAuthService().isAuthenticated() === false) {
              return next.handle(requestWithHmac);
            }
            return this.refreshAndRetryRequest(requestWithHmac, next, new Error('Missing access token'));
          }

          const requestWithAuth = this.addAuthorizationHeader(requestWithHmac, token);

          return next.handle(requestWithAuth).pipe(
            catchError((error: unknown) => {
              if (!this.shouldRefreshAfter401(error, requestWithAuth)) {
                return throwError(() => error);
              }

              return this.refreshAndRetryRequest(requestWithHmac, next, error);
            }),
          );
        }),
        timeout(timeoutMs),
        catchError((error: unknown) => {
          if (error instanceof TimeoutError) {
            return throwError(
              () =>
                new HttpErrorResponse({
                  status: 408,
                  statusText: 'Request Timeout',
                  url: req.urlWithParams,
                  error: { message: `Request timed out after ${timeoutMs}ms` },
                }),
            );
          }

          return throwError(() => error);
        }),
      );
  }

  private isStaticAsset(url: string): boolean {
    const staticAssetPattern = /\.(svg|png|jpg|jpeg|gif|ico|css|js|woff2?|ttf|eot|json)$/i;
    return url.startsWith('assets/') || staticAssetPattern.test(url);
  }

  private shouldSignWithHmac(req: HttpRequest<unknown>): boolean {
    return req.method === 'POST' && !!req.body && !this.isAuthEndpoint(req.url);
  }

  private isAuthEndpoint(url: string): boolean {
    if (url.includes('/auth/users/me')) {
      return false;
    }
    if (url.includes('/auth/terms-of-use/accept/')) {
      return false;
    }
    if (url.includes('/auth/update-contact/')) {
      return false;
    }
    return url.includes('/auth/');
  }

  private isPublicEndpoint(url: string): boolean {
    return url.includes('/umbraco/api/libraryPreviewContentAPI/search');
  }

  private addHmacHeaders(req: HttpRequest<unknown>): HttpRequest<unknown> {
    const body = req.body as {
      query?: string;
      variables?: unknown;
      operationName?: string;
    } | null;

    // GraphQL path: canonical signature generated by shared service.
    if (body?.query) {
      const { timestamp, signature } = this.hmacSignatureService.generateSignature(
        body.query,
        body.variables ?? {},
        body.operationName ?? '',
      );

      return req.clone({
        setHeaders: {
          'X-Payload-Timestamp': timestamp,
          'X-Payload-Signature': signature,
        },
      });
    }

    // Non-object body cannot be signed consistently here.
    if (!body || typeof body !== 'object') {
      return req;
    }

    // Generic JSON payload signing path.
    const bodyString = JSON.stringify(body);
    const timestamp = Math.floor(Date.now() / 1000);
    const canonical = `${bodyString}\n${timestamp}`;

    // Uses existing secret source from HmacSignatureService.
    const key = CryptoJS.enc.Utf8.parse(this.hmacSignatureService['secret']);
    const hmac = CryptoJS.HmacSHA256(canonical, key);
    const signature = CryptoJS.enc.Base64.stringify(hmac);

    return req.clone({
      setHeaders: {
        'X-Payload-Timestamp': String(timestamp),
        'X-Payload-Signature': signature,
      },
    });
  }

  private addAuthorizationHeader(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    if (req.headers.has('Authorization')) {
      return req;
    }

    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private shouldRefreshAfter401(error: unknown, request: HttpRequest<unknown>): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    if (error.status !== 401) {
      return false;
    }

    // Retry only once per request chain.
    if (request.context.get(AUTH_RETRY_CONTEXT)) {
      return false;
    }

    // Never refresh-retry auth endpoints to avoid auth-loop recursion.
    return !this.isAuthEndpoint(request.url);
  }

  private refreshAndRetryRequest(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    originalError: unknown,
  ): Observable<HttpEvent<unknown>> {
    return this.getAuthService()
      .refreshToken()
      .pipe(
        catchError((refreshError) => {
          // Invalid refresh token => force local logout cleanup.
          if (isRefreshTokenInvalidError(refreshError)) {
            this.getAuthService().logout();
          }

          return throwError(() => refreshError ?? originalError);
        }),
        switchMap((newToken) => {
          // Hard-fail when refresh resolves without a usable token.
          if (!newToken) {
            return throwError(() => originalError ?? new Error('Token refresh did not return an access token.'));
          }

          const retriedRequest = this.addAuthorizationHeader(request, newToken).clone({
            context: request.context.set(AUTH_RETRY_CONTEXT, true),
          });

          // Any downstream error from retried request is propagated.
          return next.handle(retriedRequest);
        }),
      );
  }

  private getAuthService(): AuthService {
    // Lazy resolve avoids DI cycles in some app setups.
    if (!this.authService) {
      this.authService = this.injector.get(AuthService);
    }

    return this.authService;
  }
}
